// ★ PRIMEIRO import, e de propósito: ele aplica o tema antes de o React
// montar. Ver o cabeçalho de `tema.ts` — a CSP do portal não aceita o
// `<script>` inline que a operação usa para isso.
import '@/portal/tema';
import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { roteadorDoPortal } from '@/portal/router';
import { usePortalAuth } from '@/portal/auth.store';
import '@/styles/index.css';

/**
 * Entrada do **bundle do portal** — `suporte.aptum.com.br`.
 *
 * Este arquivo é a raiz de um grafo de módulos que nunca toca `@/app/**` nem
 * `@/features/**`. O que ele compartilha com a operação é o que não carrega
 * informação: os tokens de CSS, os primitivos visuais (`@/components/*`) e o
 * `cn`. Rota, nome de campo, chamada de API e store de sessão são próprios.
 */

const clienteDeConsulta = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // 401 já é tratado pelo cliente HTTP, que renova e repete. Repetir aqui
      // multiplicaria as tentativas sem resolver nada.
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Restaura a sessão antes de renderizar.
 *
 * Só o refresh token sobrevive ao recarregamento; o access vive em memória.
 * Sem esta troca inicial, quem recarrega a página cairia na tela de entrada
 * mesmo com sessão válida.
 */
function Inicializacao() {
  const [pronto, setPronto] = useState(false);

  useEffect(() => {
    const guardado = usePortalAuth.getState().refreshToken;
    if (!guardado) {
      setPronto(true);
      return;
    }
    void usePortalAuth
      .getState()
      .renovar()
      .then(async (renovou) => {
        if (renovou) await usePortalAuth.getState().carregarContato();
      })
      .finally(() => setPronto(true));
    // Só na montagem: renovar a cada mudança do token faria laço.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!pronto) {
    return (
      <div className="flex min-h-full items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Carregando…</p>
      </div>
    );
  }

  return <RouterProvider router={roteadorDoPortal} />;
}

const container = document.getElementById('root');
if (!container) throw new Error('Elemento #root não encontrado em portal.html.');

/**
 * Um root por container, mesmo se este módulo reexecutar.
 *
 * No desenvolvimento o HMR pode reavaliar o entrypoint; um segundo
 * `createRoot` sobre o mesmo nó cria duas árvores React disputando o mesmo
 * DOM, e o sintoma engana: a tela parece "não reagir".
 */
declare global {
  interface Window {
    __portalRoot?: ReturnType<typeof createRoot>;
  }
}

const raiz = (window.__portalRoot ??= createRoot(container));

raiz.render(
  <StrictMode>
    <QueryClientProvider client={clienteDeConsulta}>
      <Inicializacao />
    </QueryClientProvider>
  </StrictMode>,
);
